"use strict";

const _ = require('lodash');
const d3 = require('d3');
const events = require('events');
const APIController = require('../shared/api-controller');
const {css} = require('../shared/css-list');
const {formatDate, formatNumber} = require('../shared/utils');

const ASC = /^asc$/i;
const DESC = /^desc$/i;

class Order {
	constructor(order) {
		if (_.isString(order)) {
			if (ASC.test(order)) {
				this._value = 1;
				return;
			} else if (DESC.test(order)) {
				this._value = -1;
				return;
			}
		}
		let v = Number(order);
		if (isNaN(v)) {
			throw new Error(`Invalid order: ${order}`);
		}
		this._value = (v && (v / Math.abs(v))) || 0;
	}

	get value() {
		return this._value;
	}

	get direction() {
		let v = this.value;
		if (v > 0) {
			return 'asc';
		} else if (v < 0) {
			return 'desc';
		} else {
			return '';
		}
	}

	valueOf() {
		return this.value;
	}

	toString() {
		return this.direction;
	}
}

class Column {
	constructor(... options) {
		this._options = Object.assign({}, ... options);
	}

	get options() {
		return this._options;
	}

	get key() {
		return this._options.key;
	}

	get call() {
		return this.options.call;
	}

	get order() {
		if (this._order) {
			return this._order;
		}
		if (this._options.order) {
			return new Order(this._options.order);
		}
		return new Order(0);
	}

	set order(v) {
		if (v instanceof Order) {
			this._order = v;
		} else {
			this._order = new Order(v);
		}
	}

	get title() {
		let options = this._options;
		if (options.hasOwnProperty('title')) {
			let title = options.title;
			if (title instanceof Function) {
				return title(this);
			} else {
				return title;
			}
		}
		// Default
		return String(this.key).toUpperCase();
	}

	value(row) {
		let options = this._options;
		if (options.hasOwnProperty('getter')) {
			let {getter} = options;
			return getter(row, this);
		}
		return row[this.key];
	}

	format(value) {
		let options = this._options;
		if (options.hasOwnProperty('formatter')) {
			let {formatter} = options;
			return formatter(value, this);
		}
		if (options.hasOwnProperty('numberFormat') && _.isNumber(value)) {
			return formatNumber(value, options.numberFormat);
		}
		if (options.hasOwnProperty('dateFormat') && value instanceof Date) {
			return formatDate(value, options.dateFormat);
		}
		if (value === undefined) {
			return '';
		}
		return value;
	}

	html(row) {
		return this.format(this.value(row));
	}

	css(row) {
		let options = this.options;
		if (options.hasOwnProperty('css')) {
			let c = options.css;
			if (c instanceof Function) {
				let value = this.value(row);
				return c(value, this);
			}
			return css(c);
		}
	}

	total(data) {
		let options = this._options;
		if (options.hasOwnProperty('reduceTotal')) {
			// This is preferred
			let {reduceTotal} = options;
			let total = undefined;
			for (let row of data) {
				total = reduceTotal(total, this.value(row), this);
			}
			return this.format(total);
		}

		// Legacy `options.sum` support
		if (options.sum) {
			let values = [];
			for (let row of data) {
				values.push(this.value(row));
			}
			let {sum} = options;
			if (!(sum instanceof Function)) {
				sum = _.sum;
			}
			return this.format(sum(values));
		}
	}

	sort(row) {
		let sort = this._options.sort;
		if (sort instanceof Function) {
			return sort(row, this);
		}
		if (_.isString(sort)) {
			return row[sort];
		}
		if (sort) {
			return this.value(row);
		}
	}

	sorter(row1, row2) {
		if (!this.isSortable) {
			return 0;
		}
		let order = this.order.value;
		if (!order) {
			return 0;
		}
		let {sorter, sort} = this._options;
		let v1, v2;
		if (sorter instanceof Function) {
			v1 = sort(row1, this);
			v2 = sort(row2, this);
		} else {
			v1 = this.value(row1);
			v2 = this.value(row2);
		}
		let s = 0;
		if (sorter instanceof Function) {
			s = sorter(v1, v2);
		} else {
			if (!v1 && !v2) {
				s = 0;
			} else if (v1 < v2 || v1 === undefined) {
				s = -1;
			} else if (v1 > v2 || v2 === undefined) {
				s = 1;
			}
		}
		return s * order;
	}

	get isSortable() {
		let options = this._options;
		if (options.hasOwnProperty('sort')) {
			let sort = options.sort;
			return sort !== false;
		}
		return true;
	}
}

class Cell {
	constructor(column, data, options) {
		this.column = column;
		this.data = data;
		this.options = options;
	}

	css() {
		return this.column.css(this.data);
	}

	html() {
		return this.column.html(this.data);
	}

	value() {
		return this.column.value(this.data);
	}

	static apply(cell) {
		let selector = d3.select(this);
		selector.attr('class', function () { return cell.css(); });
		if (_.isFunction(cell.column.call)) {
			selector.call(cell.column.call);
		} else {
			selector.html(function () { return cell.html(); });
		}
	}
}

class SimpleTable extends events.EventEmitter {
	constructor(el, ... options) {
		super();
		this._data = null;
		this._columns = [];
		this._el = el;

		options = this._options = Object.assign({
			id: _.uniqueId('cm-table-'),
			css: 'cm-table table table-striped',
			numberFormat: '0,0',
			// ISO8601 will be used, if it's falsy
			dateFormat: null
		}, ... options);

		if (!_.isFunction(options.formatNumber)) {
			options.formatNumber = _.partialRight(formatNumber, options.formatNumber);
		}
		if (!_.isFunction(options.formatDate)) {
			options.formatDate = _.partialRight(formatDate, options.formatDate);
		}

		_.bindAll(this,
			'prepareCells', 'prepareColumn',
			'drawRow', 'updateRow', 'drawHead', 'drawHeadCell', 'updateHead',
			'draw', 'redraw');
	}

	get options() {
		return this._options;
	}

	get columns() {
		return this._columns;
	}

	get data() {
		return this._data;
	}

	get el() {
		return this._el;
	}

	prepareData(data) {
		this._data = data;
	}

	static * passColumnOptions() {
		yield 'getter';
		yield 'formatter';
		yield 'numberFormat';
		yield 'dateFormat';
	}

	prepareColumn(column) {
		if (column instanceof Column) {
			return column;
		}
		if (_.isString(column)) {
			column = {key: column};
		}
		let options = Object.assign(
			_.pick(this.options, ... this.constructor.passColumnOptions()),
			column
		);
		return new Column(options);
	}

	prepareColumns(columns) {
		this._columns = columns.map(this.prepareColumn);
		return this._columns;
	}

	drawHeadCell(selector) {
		selector.append('span').text(function (column) {
			return column.title;
		});
		return selector;
	}

	drawHead(selector) {
		selector.append('thead').append('tr').selectAll('th')
			.data(this.columns).enter()
			.append('th').call(this.drawHeadCell);
		return selector;
	}

	updateHead(selector) {
		return selector;
	}

	prepareCells(row) {
		// Need more fields?
		let options = _.pick(this.options, 'numeralFormat');
		let cells = [];
		for (let c of this.columns) {
			cells.push(new Cell(c, row, options));
		}
		return cells;
	}

	drawRow(selector) {
		let tr = selector.attr('class', function (d, i) { return d.class || (i % 2 ? 'odd' : 'even'); });
		let td = tr.selectAll('td').data(this.prepareCells).enter().append('td').each(Cell.apply);
		return selector;
	}

	updateRow(selector) {
		selector.selectAll('td').data(this.prepareCells).each(Cell.apply);
		return selector;
	}

	draw(data) {
		this.prepareColumns(this.options.columns);
		this.prepareData(data);

		let table = this.table = d3.select(this.el)
			.append('table')
			.attr('class', this.options.css);

		// Draw head
		this.drawHead(table);

		// Draw rows
		table.append('tbody').selectAll('tr')
			.data(this.data).enter()
			.append('tr').call(this.drawRow);
		return this;
	}

	redraw(data) {
		this.prepareData(data || this.data);
		let table = this.table;

		// Update head
		this.updateHead(table);

		// Update/add/remove rows
		let trs = table.select('tbody').selectAll('tr').data(this.data);
		trs.call(this.updateRow);
		trs.enter().append('tr').call(this.drawRow);
		trs.exit().remove();

		return this;
	}
}

class SortableTable extends SimpleTable {
	constructor(el, ... options) {
		super(el, ... options);
		_.bindAll(this, 'headCellClass', 'headCellClickHandler', 'sorter');
	}

	static * passColumnOptions() {
		yield * super.passColumnOptions();
		yield 'sort';
		yield 'sorter';
	}

	sorter(row1, row2) {
		for (let c of this.columns) {
			let s = c.sorter(row1, row2);
			if (s) {
				return s;
			}
		}
		return 0;
	}

	prepareData(data) {
		data = [... data];
		data.sort(this.sorter);
		this._data = data;
	}

	headCellClass(column) {
		if (!column.isSortable) {
			return '';
		}
		return `sort ${column.order.direction}`;
	}

	headCellClickHandler(column) {
		let currentOrder = column.order.value;
		let newOrder = (currentOrder + 2) % 3 - 1;
		for (let c of this.columns) {
			c.order = 0;
		}
		column.order = newOrder;
		this.redraw();
	}

	drawHeadCell(selector) {
		selector.attr('class', this.headCellClass);
		let title = selector
			.append('span').text(_.property('title'));
		title.on('click', this.headCellClickHandler);
		return selector;
	}

	updateHead(selector) {
		selector
			.select('thead').select('tr').selectAll('th')
			.attr('class', this.headCellClass);
		return selector;
	}
}

class TotalsTable extends SortableTable {
	constructor(el, ... options) {
		super(el, ... options);
		_.bindAll(this, 'drawFooter', 'updateFooter', 'getColumnTotal');
	}

	static * passColumnOptions() {
		yield * super.passColumnOptions();
		yield 'reduceTotal';
	}

	getColumnTotal(column) {
		return column.total(this.data);
	}

	drawFooter(selector) {
		let footerData = _.map(this.columns, this.getColumnTotal);
		selector.append('tfoot').append('tr').selectAll('td')
			.data(footerData).enter()
			.append('td')
			.html(_.identity);
	}

	updateFooter(selector) {
		let footerData = _.map(this.columns, this.getColumnTotal);
		selector.select('tfoot').select('tr').selectAll('td')
			.data(footerData).html(_.identity);
	}

	draw(data) {
		super.draw(data);
		this.drawFooter(this.table);
	}

	redraw(data) {
		super.redraw(data);
		this.updateFooter(this.table);
	}
}

const TABLE_API_CONTROLLER_TEMPLATE = require('../templates/table.html');

class TableAPIController extends APIController {
	constructor(options) {
		super(Object.assign({template: TABLE_API_CONTROLLER_TEMPLATE}, ... arguments));
	}

	createTable() {
		throw new Error('Not implemented');
	}

	getRows(options) {
		try {
			return options.data.data;
		} catch (err) {
			return null;
		}
	}

	drawTable(rows) {
		let table = this.table;
		if (table) {
			table.redraw(rows);
		} else {
			table = this.createTable();
			table.draw(rows);
			this.table = table;
		}
	}

	destroyTable() {
		this.table = null;
		this.container.innerHTML = 'No data';
	}

	updateTable(options) {
		let rows = this.getRows(options);
		if (rows) {
			this.drawTable(rows);
		} else {
			this.destroyTable();
		}
	}

	draw(options) {
		options = super.draw(options);
		this.updateTable(options);
		return options;
	}

	redraw(options) {
		this.updateTable(options);
		return options;
	}
}

module.exports = {
	Cell, Column, SimpleTable, Table: SimpleTable,
	SortableTable, TotalsTable, TableAPIController};